/**
 * Reporting Central — a report while someone is still building it.
 *
 * A "recipe" is the whole report in one plain object: which subject one row
 * stands for, which columns in which order, the filters, the sort rules and
 * which columns are totalled. The builder holds one of these in state and these
 * pure functions are the only things that change it, so the screen never has to
 * hand-roll the same edit twice.
 *
 * `toServer` is the seam: the builder lets a person leave a filter half-typed
 * while they think, but a half-typed filter must never be sent — the server
 * would refuse the whole preview over a row the person had not finished. It is
 * dropped here, at the boundary, rather than guessed at on either side.
 */

// Comparisons that need nothing typed after them.
export const OPS_WITHOUT_VALUE = ['is_empty', 'is_not_empty'];
// Comparisons that need two values (a from and a to).
export const OPS_WITH_PAIR = ['between'];
// Comparisons that take a list of values.
export const OPS_WITH_LIST = ['in'];

export const opTakesNoValue = (op) => OPS_WITHOUT_VALUE.includes(op);
export const opTakesPair = (op) => OPS_WITH_PAIR.includes(op);
export const opTakesList = (op) => OPS_WITH_LIST.includes(op);

export const emptyRecipe = (subjectKey = '') => ({
    name: '',
    description: '',
    subject: subjectKey,
    columns: [],
    filters: [],
    sort: [],
    totals: [],
});

export const hasColumn = (recipe, key) => (recipe.columns || []).some((c) => c.key === key);

/** Add a data point as the last column. Adding one twice does nothing. */
export const addColumn = (recipe, key, header = '') => {
    if (!key || hasColumn(recipe, key)) return recipe;
    return { ...recipe, columns: [...(recipe.columns || []), { key, header }] };
};

/** Take a column out — and with it the total that was riding on it. */
export const removeColumn = (recipe, key) => ({
    ...recipe,
    columns: (recipe.columns || []).filter((c) => c.key !== key),
    totals: (recipe.totals || []).filter((t) => t !== key),
});

/** Move a column to a new position in the row. Out-of-range moves do nothing. */
export const moveColumn = (recipe, fromIndex, toIndex) => {
    const columns = [...(recipe.columns || [])];
    const inRange = (i) => Number.isInteger(i) && i >= 0 && i < columns.length;
    if (!inRange(fromIndex) || !inRange(toIndex) || fromIndex === toIndex) return recipe;
    const [moved] = columns.splice(fromIndex, 1);
    columns.splice(toIndex, 0, moved);
    return { ...recipe, columns };
};

/**
 * Give a column its own heading. An empty heading is not a heading: it is
 * cleared, and the server falls back to the data point's own label — which is
 * exactly what "put it back" means to the person who just emptied the box.
 */
export const renameColumn = (recipe, key, header) => ({
    ...recipe,
    columns: (recipe.columns || []).map((c) => (
        c.key === key ? { ...c, header: String(header ?? '').trim() } : c
    )),
});

/** A filter row is only worth sending once it names a point, a comparison and a value. */
export const isCompleteFilter = (row) => {
    if (!row || !row.key || !row.op) return false;
    if (opTakesNoValue(row.op)) return true;
    if (opTakesPair(row.op)) {
        const pair = Array.isArray(row.value) ? row.value : [];
        return pair.length === 2 && pair.every((v) => v !== '' && v !== null && v !== undefined);
    }
    if (opTakesList(row.op)) {
        return Array.isArray(row.value)
            && row.value.some((v) => String(v ?? '').trim() !== '');
    }
    return row.value !== '' && row.value !== null && row.value !== undefined;
};

const filterValue = (row) => {
    if (opTakesPair(row.op)) return [row.value[0], row.value[1]];
    if (opTakesList(row.op)) {
        return row.value.map((v) => String(v).trim()).filter((v) => v !== '');
    }
    return row.value;
};

/**
 * The recipe in the shape the server takes: half-typed filter rows dropped, and
 * totals dropped once the column they totalled is no longer in the report (that
 * happens the moment someone removes a column or changes the subject).
 */
export const toServer = (recipe) => {
    const columns = (recipe.columns || []).map((c) => (
        c.header ? { key: c.key, header: c.header } : { key: c.key }
    ));
    const columnKeys = columns.map((c) => c.key);
    return {
        subject: recipe.subject || '',
        columns,
        filters: (recipe.filters || []).filter(isCompleteFilter).map((row) => (
            opTakesNoValue(row.op)
                ? { key: row.key, op: row.op }
                : { key: row.key, op: row.op, value: filterValue(row) }
        )),
        sort: (recipe.sort || [])
            .filter((rule) => rule && rule.key)
            .map((rule) => ({ key: rule.key, dir: rule.dir === 'desc' ? 'desc' : 'asc' })),
        totals: (recipe.totals || []).filter((key) => columnKeys.includes(key)),
    };
};

/**
 * Has anything been changed since it was last saved? Compared on what would
 * actually be sent, so a half-typed filter row does not count as a change the
 * person would lose.
 */
export const isDirty = (a, b) => {
    if (!a || !b) return Boolean(a) !== Boolean(b);
    const shape = (recipe) => JSON.stringify({
        name: String(recipe.name || '').trim(),
        description: String(recipe.description || '').trim(),
        ...toServer(recipe),
    });
    return shape(a) !== shape(b);
};

/** A saved report, read back from the server, as a recipe the builder can edit. */
export const fromDetail = (detail) => ({
    name: detail?.name || '',
    description: detail?.description || '',
    subject: detail?.subject || '',
    columns: (detail?.columns || []).map((c) => ({ key: c.key, header: c.header || '' })),
    filters: (detail?.filters || []).map((f) => ({
        key: f.key, op: f.op, value: f.value === null || f.value === undefined ? '' : f.value,
    })),
    sort: (detail?.sort || []).map((s) => ({ key: s.key, dir: s.dir === 'desc' ? 'desc' : 'asc' })),
    totals: [...(detail?.totals || [])],
});
